import {PcoProjetoModel} from "@/models/prestacaocontas/pcoprojeto/pcoprojeto.model";
import api from "@/services/axios/api";
import {AxiosResponse} from "axios";
import {DataSource, FiltrosModel} from "@/datasources/consulte-grid";
import {RegisterOutput} from "@/models/register-output";
import TextoValor from "@/models/texto-valor";

type DropDownPcoProjetoOptions = {
 somenteNaoConcluido?: boolean;
 situacao?: number | null;
 gesEscolaId?: number | null;
};

class PcoProjetoService {
 async dadosIndexPaginado(pagina: number, itensPorPagina: number, filters: FiltrosModel[], gesEscolaId: number | null): Promise<AxiosResponse<DataSource<PcoProjetoModel>>> {
  const result = await api.post<DataSource<PcoProjetoModel>>(
   "/PCoProjeto/RetornoPaginado",
   {
    rowsPerPage: itensPorPagina,
    pageNumber: pagina,
    filters: filters,
   },
   {
    params: {
     GesEscolaId: gesEscolaId,
    },
   }
  );

  result.data.rows = (result.data.rows ?? []).map((item: any) => this.normalizar(item));
  return result;
 }

 inserirAtualizar(pcoProjeto: PcoProjetoModel): Promise<AxiosResponse<RegisterOutput>> {
  return api.post("/PCoProjeto", pcoProjeto);
 }

 remover(id: number): Promise<AxiosResponse> {
  return api.delete("/PCoProjeto/" + id);
 }

 atualizar(pcoProjeto: PcoProjetoModel): Promise<AxiosResponse> {
  return api.put("/PCoProjeto", pcoProjeto);
 }

 async get(id: number): Promise<PcoProjetoModel> {
  const result = await api.get<PcoProjetoModel>(`/PCoProjeto/${id.toString()}`);
  return this.normalizar(result.data);
 }

 async DropDownListPcoProjeto(options: DropDownPcoProjetoOptions = {}) : Promise<TextoValor[]> {
  if (!options.somenteNaoConcluido && options.situacao === undefined && !options.gesEscolaId) {
   const result = await api.get("/PCoProjeto/DropDownList");
   const itens = result.data.map((item: any) => ({
    valor: item.valor ?? item.id,
    texto: item.texto ?? item.descricao,
   }));

   return [
    { valor: 0, texto: "" },
    ...itens,
   ];
  }

  const params: any = {};
  if (options.situacao !== undefined && options.situacao !== null)
   params.Situacao = options.situacao;
  else if (options.somenteNaoConcluido)
   params.Situacao = 0;
  if (options.gesEscolaId)
   params.GesEscolaId = options.gesEscolaId;

  const result = await api.post<DataSource<PcoProjetoModel>>(
   "/PCoProjeto/RetornoPaginado",
   {
    rowsPerPage: 5000,
    pageNumber: 0,
    filters: [],
    sortDataField: "descricao",
    sortOrder: "asc",
   },
   { params }
  );

  const itens = (result.data.rows ?? []).map((item: any) => this.normalizar(item)).map((item: PcoProjetoModel) => ({
   valor: item.pcoProjetoId ?? 0,
   texto: item.numero ? `${item.numero} - ${item.descricao ?? ""}` : item.descricao ?? "",
  }));

  return [
   { valor: 0, texto: "" },
   ...itens,
  ];
 }

 private normalizarData(value: any): string | null {
  if (!value)
   return null;

  const texto = String(value);
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso)
   return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const data = new Date(value);
  if (Number.isNaN(data.getTime()))
   return null;

  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
 }

 private normalizar(item: any): PcoProjetoModel {
  const situacao = item.situacao ?? item.Situacao ?? 0;
  const concluido = item.concluido ?? item.Concluido ?? situacao === 1;

  return {
   pcoProjetoId: item.pcoProjetoId ?? item.pCoProjetoId ?? item.PCoProjetoId ?? 0,
   gesEscolaId: item.gesEscolaId ?? item.GesEscolaId ?? null,
   pcoOrigemRecursoId: item.pcoOrigemRecursoId ?? item.pCoOrigemRecursoId ?? item.PCoOrigemRecursoId ?? null,
   valor: item.valor ?? item.Valor ?? null,
   periodoPrestacaoContas: item.periodoPrestacaoContas ?? item.PeriodoPrestacaoContas ?? 0,
   numero: item.numero ?? item.Numero ?? "",
   descricao: item.descricao ?? item.Descricao ?? "",
   objeto: item.objeto ?? item.Objeto ?? "",
   resultado: item.resultado ?? item.Resultado ?? null,
   dataAssinatura: this.normalizarData(item.dataAssinatura ?? item.DataAssinatura),
   dataPublicacao: this.normalizarData(item.dataPublicacao ?? item.DataPublicacao),
   dataVigencia: this.normalizarData(item.dataVigencia ?? item.DataVigencia),
   dataConclusao: this.normalizarData(item.dataConclusao ?? item.DataConclusao),
   situacao,
   concluido,
  };
 }
}

export default new PcoProjetoService();
